import {
  FormField,
  FormFieldType,
  IconSelectFieldType,
  IconSelectOption,
  mapEnumToSelectOptions,
  numberBetweenValidator,
  requiredStringValidation,
} from '@hclinear/hc-linear-react';
import { t } from '@localization/i18n';
import { Box } from '@mui/material';
import React from 'react';
import { SORTED_COLOR_DTOS } from '@components/announcement/util/announcementColorSwatch.util';
import {
  ColorOptionSwatch,
  isElementResizable,
  isImageType,
  isTextElementType,
} from '@/components';
import {
  AnchorXDTO,
  AnchorYDTO,
  ElementDTOUnion,
  ElementTypeDTO,
  FontSelectDTO,
  ImageResizeType,
} from '@/model';

export const editElementModalFormFields = (
  fetchedRasterFonts: FontSelectDTO[],
  DISPLAY_WIDTH: number,
  DISPLAY_HEIGHT: number,
  minContentWidth: number,
  minContentHeight: number,
  element: ElementDTOUnion,
  elementType: ElementTypeDTO,
  onFieldBlur: () => void,
  rasterFontSelectOptions?: IconSelectOption[],
  onFontNameChange?: (changedValue: string) => IconSelectOption[],
  onImageModalOpen?: (elementType: ElementTypeDTO) => void,
): FormField[] => {
  if (!element) return [];

  const isAnnouncementElement = elementType === ElementTypeDTO.ANNOUNCEMENT;
  const isTextElement = isTextElementType(elementType);
  const isTextValueNotEditableTextElement = [ElementTypeDTO.CLOCK].includes(elementType);

  const isImageElement = isImageType(elementType);

  const isResizableElement = isElementResizable(element);

  const imageBackgroundColorFormField: FormField = {
    type: FormFieldType.IconSelect,
    name: 'backgroundColor',
    label: 'announcements.table.header.background-color',
    options: SORTED_COLOR_DTOS.map((color) => ({
      value: color,
      label: <ColorOptionSwatch color={color} />,
    })),
    onChange: () => onFieldBlur(),
    xs: 6,
  };

  const commonFields: FormField[] = [
    {
      type: FormFieldType.Number,
      label: 'layout.x-coordinate',
      name: 'x',
      xs: 6,
      validation: numberBetweenValidator(0, DISPLAY_WIDTH - element.width),
      onBlur: () => onFieldBlur(),
    },
    {
      type: FormFieldType.Number,
      label: 'layout.y-coordinate',
      name: 'y',
      xs: 6,
      validation: numberBetweenValidator(0, DISPLAY_HEIGHT - element.height),
      onBlur: (fieldName) => onFieldBlur(),
    },
    {
      type: FormFieldType.Number,
      label: 'common.width',
      name: 'width',
      postFix: 'px',
      xs: 6,
      onBlur: (fieldName) => onFieldBlur(),
      validation: isResizableElement
        ? numberBetweenValidator(0, DISPLAY_WIDTH - element.x)
        : numberBetweenValidator(element.width, element.width),
      disabled: element.elementType === ElementTypeDTO.TIMETABLE,
    },
    {
      type: FormFieldType.Number,
      label: 'common.height',
      name: 'height',
      postFix: 'px',
      xs: 6,
      onBlur: (fieldName) => onFieldBlur(),
      validation: isResizableElement
        ? numberBetweenValidator(0, DISPLAY_HEIGHT - element.y)
        : numberBetweenValidator(element.height, element.height),
      disabled: element.elementType === ElementTypeDTO.TIMETABLE,
    },
    {
      type: FormFieldType.Select,
      label: 'common.horizontal-alignment',
      name: 'anchorX',
      xs: 6,
      options: mapEnumToSelectOptions(AnchorXDTO, 'enum.'),
      onChange: () => onFieldBlur(),
    },
    {
      type: FormFieldType.Select,
      label: 'common.vertical-alignment',
      name: 'anchorY',
      xs: 6,
      options: mapEnumToSelectOptions(AnchorYDTO, 'enum.'),
      onChange: () => onFieldBlur(),
    },
  ];

  const resizeTypeFormField: FormField = {
    type: FormFieldType.Select,
    label: 'common.resize-type',
    name: 'imageResizeType',
    options: Object.keys(ImageResizeType).map((key) => ({
      label: t(`common.image-size.${key.toLowerCase()}`),
      value: key,
    })),
    validation: requiredStringValidation(),
    onChange: onFieldBlur,
    xs: 12,
  };

  if (isImageElement) {
    const updateImageButtonFormField: FormField = {
      type: FormFieldType.Button,
      label: 'common.change-image',
      onClick: () => onImageModalOpen(elementType),
      xs: 6,
    };

    return [
      ...commonFields,
      resizeTypeFormField,
      imageBackgroundColorFormField,
      updateImageButtonFormField,
    ];
  }

  if (!isAnnouncementElement && !isTextElement) {
    return commonFields;
  }

  const rasterFontPrimarySelect: IconSelectFieldType = {
    type: FormFieldType.IconSelect,
    name: 'ttFontName',
    label: 'common.font-type',
    options: fetchedRasterFonts?.map((rasterFont) => ({
      value: rasterFont.ttFontName,
      label: (
        <Box
          sx={{
            width: 110,
            height: 24,
          }}
        >
          <img
            alt={`${rasterFont.ttFontName}`}
            src={`data:image/png;base64,${rasterFont.content}`}
          />
        </Box>
      ),
    })),
    validation: requiredStringValidation(),
    xs: 8,
  };

  const rasterFontDependentSelect: IconSelectFieldType = {
    type: FormFieldType.IconSelect,
    name: 'rasterFontId',
    label: 'common.font-size',
    options: rasterFontSelectOptions,
    validation: requiredStringValidation(),
    onChange: () => onFieldBlur(),
    xs: 4,
  };

  const fontColorFormField: FormField = {
    type: FormFieldType.IconSelect,
    name: 'fontColor',
    label: 'announcements.table.header.font-color',
    options: SORTED_COLOR_DTOS.map((color) => ({
      value: color,
      label: <ColorOptionSwatch color={color} />,
    })),
    validation: requiredStringValidation(),
    xs: 6,
    onChange: () => onFieldBlur(),
  };

  const linkedFontFormField: FormField = {
    type: FormFieldType.LinkedIconSelect,
    primarySelect: rasterFontPrimarySelect,
    dependentSelect: rasterFontDependentSelect,
    fetchDependentOptions: async (selectedValue: string) =>
      onFontNameChange(selectedValue),
    xs: 12,
    name: 'linked',
  };

  const fontStyleFormFields: FormField[] = [
    fontColorFormField,
    imageBackgroundColorFormField,
    linkedFontFormField,
  ];

  if (isAnnouncementElement) {
    const announcementFormFields: FormField[] = [
      {
        type: FormFieldType.Text,
        name: 'announcement.name',
        label: 'common.name',
        xs: 12,
        validation: requiredStringValidation(),
        onBlur: () => onFieldBlur(),
      },
    ];
    return [
      ...announcementFormFields,
      ...commonFields,
      { ...imageBackgroundColorFormField, xs: 12 },
      resizeTypeFormField,
    ];
  }

  if (!isTextElement || isTextValueNotEditableTextElement) {
    return [...commonFields, ...fontStyleFormFields];
  }

  const textValueFormField: FormField = {
    type: FormFieldType.Text,
    name: 'textValue',
    label: 'common.value',
    xs: 12,
    onBlur: () => onFieldBlur(),
  };

  return [textValueFormField, ...commonFields, ...fontStyleFormFields];
};
